/*
 * The map uses the Denmark paths VERBATIM from src/assets/dk_small.svg
 * (viewBox 0 0 1000 810), including the source's two-layer look: a dark
 * base silhouette behind a brighter land layer. Node positions were
 * projected from geographic coordinates and verified against the landmass.
 */

import { DENMARK_BASE_PATHS, DENMARK_LAND_PATHS } from "./denmarkPaths";
import type { LogoWallContent, MapNodeId, ConsortiumId } from "../i18n/types";


/* University and datacenter nodes, positioned by lat/lon projected onto the
 * map. Consortium universities (SDU, AU, AAU) are highlighted; the Bitten
 * datacenter in Sønderborg gets its own accent. Node ids are structural and
 * shared with the content dictionaries; names live there. */
interface MapNode {
  id: MapNodeId;
  x: number;
  y: number;
  kind: "consortium" | "university" | "datacenter";
}

/* Geo → SVG projection for node positions (viewBox 0 0 1000 810):
 *   x = 130.50 × lon − 1018.48
 *   y = 13512.55 − 233.48 × lat
 * Least-squares fit to the nodes below (the map is equirectangular,
 * standard parallel ≈ 56.2°N); accurate to ±7px — Copenhagen-area nodes
 * sit ~5px south of the raw fit, so verify projected points against
 * the landmass and neighboring nodes before committing. Example:
 * DCAI/Gefion 55.7752°N 12.4945°E → raw (612.0, 490.3), placed at
 * (611.3, 495.5) to preserve its true offset from the DTU node. */
const MAP_NODES: MapNode[] = [
  // Consortium universities
  { id: "aalborg", x: 280.0, y: 197.4, kind: "consortium" },
  { id: "aarhus", x: 315.9, y: 403.0, kind: "consortium" },
  { id: "sdu", x: 339.0, y: 578.6, kind: "consortium" },
  // Other Danish universities
  { id: "ku", x: 619.9, y: 513.9, kind: "university" },
  { id: "dtu", x: 614.8, y: 493.1, kind: "university" },
  { id: "ruc", x: 570.0, y: 523.2, kind: "university" },
  // Datacenter
  { id: "bitten", x: 263.3, y: 691.8, kind: "datacenter" },
  // { id: "gefion", x: 611.3, y: 495.5, kind: "datacenter" },
  { id: "gefion", x: 594.2, y: 501.0, kind: "datacenter" },
];

const CONSORTIUM_LOGOS: { id: ConsortiumId; src: string; className: string }[] = [
  {
    id: "aalborg",
    src: "/assets/logo-wall/AAU.png",
    className: "",
  },
  {
    id: "aarhus",
    src: "/assets/logo-wall/AU.png",
    className: "",
  },
  {
    id: "sdu",
    src: "/assets/logo-wall/SDU.png",
    className: "",
  },
];

export function renderLogoWall(t: LogoWallContent): HTMLElement {
  const root = document.createElement("section");
  root.className = "section section--tight consortium";
  root.id = "consortium";
  root.innerHTML = `
    <div class="page">
      <div class="consortium__intro" data-reveal>
        <div>
          <h2 class="section__title">${t.title}</h2>
          <p>
            ${t.introLead}<br /><strong>${t.introStrong}</strong>
          </p>
          <a class="consortium__link" href="https://interactivehpc.dk/" target="_blank" rel="noreferrer">
            ${t.linkLabel} <span aria-hidden="true">↗</span>
          </a>
        </div>
        <figure class="consortium__map">
          <svg viewBox="0 0 1000 810" role="img" aria-labelledby="denmark-map-title denmark-map-description">
            <title id="denmark-map-title">${t.mapTitle}</title>
            <desc id="denmark-map-description">${t.mapDescription}</desc>
            <path class="denmark-map__grid" d="M0 100h1000M0 200h1000M0 300h1000M0 400h1000M0 500h1000M0 600h1000M0 700h1000M100 0v810M200 0v810M300 0v810M400 0v810M500 0v810M600 0v810M700 0v810M800 0v810M900 0v810" />
            ${DENMARK_BASE_PATHS.map((d) => `<path class="denmark-map__land denmark-map__land" d="${d}" />`).join("")}
            ${DENMARK_LAND_PATHS.map((d) => `<path class="denmark-map__land" d="${d}" />`).join("")}
            <path class="denmark-map__route" d="M280 197.4L315.9 403L339 578.6L263.3 691.8" />
            <path class="denmark-map__route denmark-map__route--secondary" d="M339 578.6L570 523.2M339 578.6L619.9 513.9M315.9 403L570 523.2" />
            <g class="denmark-map__nodes denmark-map__nodes--datacenter">
              ${MAP_NODES.filter((n) => n.kind === "datacenter").map((n) => `<circle cx="${n.x}" cy="${n.y}" r="11"><title>${t.nodeNames[n.id]}</title></circle>`).join("")}
            </g>
            <g class="denmark-map__nodes denmark-map__nodes--university">
              ${MAP_NODES.filter((n) => n.kind === "university").map((n) => `<circle cx="${n.x}" cy="${n.y}" r="9"><title>${t.nodeNames[n.id]}</title></circle>`).join("")}
            </g>
            <g class="denmark-map__nodes denmark-map__nodes--highlighted">
              ${MAP_NODES.filter((n) => n.kind === "consortium").map((n) => `<circle cx="${n.x}" cy="${n.y}" r="11"><title>${t.nodeNames[n.id]}</title></circle>`).join("")}
            </g>
            <text class="denmark-map__label" x="30" y="60">${t.mapLabelPrimary}</text>
            <text class="denmark-map__label denmark-map__label--muted" x="30" y="800">${t.mapLabelSecondary}</text>
          </svg>
        </figure>
      </div>
      <div class="logo-wall__grid" aria-label="${t.logoAriaLabel}">
        ${CONSORTIUM_LOGOS.map((logo, index) => `
          <a class="logo-wall__mark ${logo.className}" href="${t.logoHrefs[logo.id]}" target="_blank" rel="noreferrer" aria-label="${t.logoNames[logo.id]}" data-reveal style="--reveal-delay:${index * 60}ms">
            <img src="${logo.src}" alt="${t.logoNames[logo.id]}" width="210" height="58" />
          </a>
        `).join("")}
      </div>
    </div>
  `;
  return root;
}
